<script setup lang="ts">
import { ref } from 'vue';
import { ModalDialog, QuestionPhone, QuestionEmail, router, SecondaryButton, ContactPhone, NeutralButton } from '@hfc/ui-core';
import { useForm } from 'vee-validate';
import { useHandleApiError } from '@/shared/infrastructure/composition/useHandleApiError';
import { MedicareConsent } from '@/consent/medicare/domain/MedicareConsent';
import { CONTACT_PREFERENCE_TYPES } from '@hfc/common/src/types/ContactPreferenceTypes';
import { useMedicareConsentStore } from '@/consent/medicare/infrastructure/composition/useMedicareConsentStore';

interface EmitsInterface {
  (event: 'close'): void;
}

const emits = defineEmits<EmitsInterface>();

const { handleServicesApiError } = useHandleApiError();

const { medicareConsentStore, medicareConsentState } = useMedicareConsentStore();

const { handleSubmit } = useForm();
const runValidations = handleSubmit(() => true);

const phoneRef = ref<{ handleSubmit: () => Promise<boolean> } | null>(null);
const emailRef = ref<{ handleSubmit: () => Promise<boolean> } | null>(null);

const email = ref(medicareConsentState.value.application.contactInfo.email);
const phone = ref(medicareConsentState.value.application.contactInfo.phone);

const confirmSaveAndExit = async (saveAndExitContactPreference?: CONTACT_PREFERENCE_TYPES): Promise<void> => {
  const valid = await runValidations();
  const emailValid = await emailRef.value?.handleSubmit();
  const phoneValid = await phoneRef.value?.handleSubmit();

  if (!valid || !emailValid || !phoneValid) {
    return;
  }
  if (email.value !== medicareConsentState.value.application.contactInfo.email) {
    await medicareConsentStore.updateContactInfo({ email: email.value });
  }
  if (phone.value !== medicareConsentState.value.application.contactInfo.phone) {
    await medicareConsentStore.updateContactInfo({ phone: phone.value });
  }

  const result = await medicareConsentStore.saveAndExit(
    medicareConsentState.value.application.id,
    saveAndExitContactPreference === CONTACT_PREFERENCE_TYPES.TEXT ? null : medicareConsentState.value.application.contactInfo.email,
    saveAndExitContactPreference === CONTACT_PREFERENCE_TYPES.EMAIL ? null : medicareConsentState.value.application.contactInfo.phone,
    medicareConsentState.value.application as MedicareConsent);
  if (result.error) {
    const isValidationError = result.error.response?.status === 400;
    handleServicesApiError(result.error, false,
      isValidationError ? 'There was an error validating the fields. Please review and try again.' : 'Error saving and exiting. Please try again.'
    );
  }

  if (result.success) {
    localStorage.setItem('medicareSaveAndExit', 'true');
    const type = medicareConsentState.value.application.appointment_required ? 'appointment' : 'consent';
    router.push({ name: 'MedicareSaveAndExit', params: { type, uuid: medicareConsentState.value.application.id } });
    emits('close');
  }
};
</script>

<template>
  <ModalDialog title="Important!" :loading="medicareConsentState.loading" @close="emits('close')" @confirm="confirmSaveAndExit" confirm-button="Yes" cancel-button="Close" :close-on-confirm="false" :show-footer="false">
    <template #content>
      <div class="flex">
        <p>Are you sure you want to save and exit? If so, everything you’ve entered so far will be saved and you can opt to receive a link to resume your Scope of Appointment via an SMS message (text message via phone), an email, or both.</p>
      </div>
      <div class="flex w-full justify-center space-x-4">
        <div class="flex flex-col flex-1">
          <label class="font-medium mb-1 whitespace-nowrap text-sky-700 block">
            Email <span class="font-serif">*</span>
          </label>
          <QuestionEmail ref="emailRef" :value="email" :disable-placeholder="true" @update:value="(v: string) => { email = v; }" id="medicare-email" name="medicare-email" />
        </div>
        <div class="flex flex-col flex-1">
          <label class="font-medium mb-1 whitespace-nowrap text-sky-700 block">
            Phone <span class="font-serif">*</span>
          </label>
          <QuestionPhone ref="phoneRef" :value="{ number: phone }" phone-input-placeholder="Phone" @update:value="(v: ContactPhone) => { phone = v.number; }" id="medicare-phone" name="medicare-phone" :hide-ext="true" :hide-type="true" />
        </div>
      </div>
      <div class="flex w-full border-t border-slate-200">
        <div class="flex flex-row w-full justify-end space-x-2 mt-3">
          <SecondaryButton label="Sms" class="rounded-md uppercase" :is-loading="medicareConsentState.loading" @click="confirmSaveAndExit(CONTACT_PREFERENCE_TYPES.TEXT)" />
          <SecondaryButton label="Email" class="rounded-md" :is-loading="medicareConsentState.loading" @click="confirmSaveAndExit(CONTACT_PREFERENCE_TYPES.EMAIL)" />
          <SecondaryButton label="Both" class="rounded-md" :is-loading="medicareConsentState.loading" @click="confirmSaveAndExit()" />
          <NeutralButton label="Close" @click="emits('close')" />
        </div>
      </div>
    </template>
  </ModalDialog>
</template>
